import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { container } from '@/container/CompositionRoot';
import type { MegaGridExternalFilter } from '@/design/MegaGrid/MegaGrid';
import { useGlobalFilters } from './useGlobalFilters';
import { globalScopeForPath } from './GlobalFilterScope';
import { GLOBAL_FIELD_META } from './GlobalFilterFieldMeta';
import type { GlobalFilterField } from './GlobalFilterTypes';
import { GlobalFilterValueService } from './GlobalFilterValueService';
import { dateRangeLabel } from './GlobalDateRangeControl';

/**
 * The active global filters as read-only display entries, for surfaces that
 * want to acknowledge them inside their own chrome (e.g. a MegaGrid's filters
 * dropdown). Scoped to the current route: only the date range / fields that
 * actually apply to this page are listed.
 */
export function useGlobalFilterSummary(): MegaGridExternalFilter[] {
    const location = useLocation();
    const scope = useMemo(() => globalScopeForPath(location.pathname), [location.pathname]);
    const { snapshot } = useGlobalFilters();
    const service = useMemo(() => container.resolve(GlobalFilterValueService), []);
    const [providersLoaded, setProvidersLoaded] = useState(false);

    useEffect(() => {
        void service.loadProviders().then(() => setProvidersLoaded(true));
    }, [service]);

    return useMemo(() => {
        const entries: MegaGridExternalFilter[] = [];
        if (scope.dateRange) {
            entries.push({ id: 'global:date', label: 'Date range', value: dateRangeLabel(snapshot.dateRange) });
        }
        const scoped = new Set<string>(scope.fields);
        for (const dimension of snapshot.dimensions) {
            if (!scoped.has(dimension.field) || dimension.values.length === 0) continue;
            const labelFor = (value: string): string => (dimension.field === 'provider_connection_id' && providersLoaded ? service.providerLabel(value) : value);
            entries.push({
                id: `global:${dimension.field}`,
                label: GLOBAL_FIELD_META[dimension.field as GlobalFilterField].label,
                value: dimension.values.map(labelFor).join(', '),
            });
        }
        return entries;
    }, [scope, snapshot, service, providersLoaded]);
}
